<template>
    <span class="role-selector">
        <Select
            :placeholder="isFetching ? '...' : elementName"
            :loading="isFetching"
            :options="roles"
            :invalid="allLoadedButNoRole"
            :disabled="!isEditing"
            v-model="selectedRole"
            optionLabel="name"
            value="identity"
            @change="changeRole"
            variant="outlined"
            dropdownIcon="-"
            v-tooltip.top="
                'These roles can be selected from what you have defined at the domain level.'
            "
        >
            <template #footer>
                <div class="p-3" @click="openRoleDialog()">
                    <Button
                        label="Add role"
                        fluid
                        severity="secondary"
                        variant="text"
                        size="small"
                        icon="pi pi-plus"
                    />
                </div>
            </template>
        </Select>
        <i
            v-if="hasConflict"
            class="pi pi-exclamation-triangle"
            style="color: orange"
            v-tooltip="'This role is already assigned to another lane.'"
        />
    </span>
</template>

<script setup lang="ts">
import { type Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import type { SelectChangeEvent } from "primevue/select";
import { computed, ref, watch } from "vue";

import { useBpmnManager } from "@/composables/bpmn-manager";
import { useEditManager } from "@/composables/edit-manager";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useProcessManager } from "@/composables/process-manager";
import { Role } from "@/composables/role-manager";
import { useLanesManager } from "@/stores/lanes-store";
import { useRolesManager } from "@/stores/roles-store";

const props = defineProps<{
    element: BPMNElement;
    bpmnManager: ReturnType<typeof useBpmnManager>;
}>();

const { isEditing } = useEditManager();
const { lanes, isFetching: isFetchingLanes } = useLanesManager();
const { roles, isLoading: isLoadingRoles } = useRolesManager();
const { isFetching: isFetchingProcess } = useProcessManager();
const { open: openRoleDialog } = useEditRoleDialog();

const hasConflict = ref(false);

const selectedRole = ref<Role>();
const elementName = computed(
    () => props.element.name || props.element.businessObject?.name || "..."
);
const elementRoleId = ref();

watch(
    [() => props.element, props.bpmnManager.xml],
    () => {
        elementRoleId.value = getBusinessObject(props.element).roleId;
    },
    { immediate: true }
);

const lane = computed(() => {
    return lanes.value?.find((l) => {
        return l.bpmn_id === getBusinessObject(props.element).id;
    });
});

const isFetching = computed(() =>
    Boolean(
        isFetchingLanes.value || isFetchingProcess.value || isLoadingRoles.value
    )
);

watch([selectedRole, props.bpmnManager.xml], async () => {
    if (isFetching.value) {
        return false;
    }
    const bpmnLanes = (await props.bpmnManager.getLanes()).map(
        getBusinessObject
    );
    hasConflict.value = bpmnLanes.some(
        (l) =>
            l.id !== getBusinessObject(props.element).id &&
            l.roleId === selectedRole.value?.identity
    );
});

// Only returns true when lanes and roles are loaded, but no selectedRole is found.
const allLoadedButNoRole = computed(() => {
    if (
        lane.value &&
        roles.value &&
        !selectedRole.value &&
        !elementRoleId.value
    ) {
        return true;
    }
    return false;
});

// Autoselect role for lane if the name matches.
watch(
    allLoadedButNoRole,
    (newValue) => {
        // Means that it's already set.
        if (!newValue) return;
        const roleByName = roles.value?.find(
            (r) => r.name === getBusinessObject(props.element).name
        );
        if (roleByName) {
            props.bpmnManager.updateElementProperties(props.element.id, {
                name: roleByName.name,
                roleId: roleByName.identity,
            });
        }
    },
    { once: true }
);

watch(
    [lane, isLoadingRoles, elementRoleId],
    ([newLane, isLoadingRoles, roleId]) => {
        if (isLoadingRoles) return;

        const role = roles.value?.find(
            (role) =>
                role.identity === roleId || role.identity === newLane?.role_id
        );
        if (role) {
            selectedRole.value = role;
        }
    },
    { immediate: true }
);

const changeRole = (event: SelectChangeEvent) => {
    const roleId = event.value.identity;
    if (!roleId) {
        return;
    }

    selectedRole.value = roles.value?.find((role) => role.identity === roleId);
    if (selectedRole.value && props.element) {
        props.bpmnManager.updateElementProperties(props.element.id, {
            name: selectedRole.value.name,
            roleId: selectedRole.value.identity,
        });
        // Make sure the change is picked up.
        props.bpmnManager.eventBus?.fire("commandStack.changed");
    }
};
</script>

<style scoped lang="scss">
.role-selector {
    display: flex;
    align-items: center;
    position: absolute;
    top: 1px;
    left: 1px;

    :deep(.p-select) {
        background: none;
        border: none;
        box-shadow: none;

        .p-select-dropdown {
            width: 0;
        }

        &.p-invalid {
            .p-select-label,
            .p-select-dropdown {
                color: var(--p-select-invalid-border-color);
            }
        }
    }
}
</style>
